import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { LANGUAGES, languageSchema, type Language } from '@kratispot/shared';

const STORAGE_KEY = 'kratispot.language';

// Every visible string lives here; adding a language is a column, not a branch.
const COPY = {
  brand: { el: 'KratiSpot', en: 'KratiSpot' },
  tagline: { el: 'βρες τη θέση σου δίπλα στη θάλασσα', en: 'find your place by the sea' },
  signIn: { el: 'Σύνδεση', en: 'Sign in' },
  signInLead: {
    el: 'Συνδεθείτε για να δείτε τον χάρτη του καταστήματός σας.',
    en: 'Sign in to reach your venue’s live map.',
  },
  continueGoogle: { el: 'Συνέχεια με Google', en: 'Continue with Google' },
  emailLabel: { el: 'Email προσωπικού', en: 'Staff email' },
  sendLink: { el: 'Στείλε μου σύνδεσμο', en: 'Email me a link' },
  linkSent: {
    el: 'Αν η διεύθυνση ανήκει σε μέλος της ομάδας, ο σύνδεσμος στάλθηκε.',
    en: 'If that address belongs to a team member, the link is on its way.',
  },
  orEmail: { el: 'ή με email', en: 'or by email' },
  signOut: { el: 'Αποσύνδεση', en: 'Sign out' },
  team: { el: 'Ομάδα & ρόλοι', en: 'Team & roles' },
  settings: { el: 'Ρυθμίσεις καταστήματος', en: 'Venue settings' },
  settingsSaved: { el: 'Οι ρυθμίσεις αποθηκεύτηκαν', en: 'Settings saved' },
  settingsFailed: { el: 'Η αποθήκευση απέτυχε', en: 'Saving failed' },
  settingsInvalid: {
    el: 'Ελέγξτε τα πεδία: κάτι δεν είναι αποδεκτό.',
    en: 'Check the fields — something here is not allowed.',
  },
  totalSpots: { el: 'Θέσεις συνολικά', en: 'Spots in total' },
  layoutSize: { el: 'Πλέγμα διάταξης', en: 'Layout grid' },
  numbering: { el: 'Αρίθμηση', en: 'Numbering' },
  staffOnlySpots: { el: 'Μόνο για το προσωπικό', en: 'Kept for staff' },
  manageOnMap: { el: 'Διαχείριση στον χάρτη', en: 'Manage on map' },
  noSpotsYet: { el: 'Καμία θέση ακόμη', en: 'No spots yet' },
  holdSettings: { el: 'Παρακράτηση κράτησης', en: 'Reservation hold' },
  holdFreeUntil: { el: 'Δωρεάν έως', en: 'Held free until' },
  holdLatest: { el: 'Μέγιστη ώρα', en: 'Latest hold hour' },
  holdStep: { el: 'Βήμα παράτασης', en: 'Extension step' },
  minutesShort: { el: 'λεπτά', en: 'min' },
  cancelPolicy: { el: 'Πολιτική ακύρωσης', en: 'Cancellation policy' },
  cancelFull: { el: 'Πλήρης επιστροφή έως (ώρες πριν)', en: 'Full refund up to (hours before)' },
  cancelPartial: { el: 'Μερική επιστροφή (%)', en: 'Partial refund (%)' },
  cancelNone: { el: 'Καμία επιστροφή εντός (ώρες)', en: 'No refund within (hours)' },
  pricing: { el: 'Τιμές ανά ζώνη', en: 'Prices per zone' },
  zoneKeyLabel: { el: 'Κωδικός ζώνης', en: 'Zone key' },
  perDayLabel: { el: 'ανά ημέρα', en: 'per day' },
  perHourLabel: { el: 'ανά επιπλέον ώρα', en: 'per extra hour' },
  addZone: { el: 'Νέα ζώνη', en: 'Add zone' },
  noZonesYet: {
    el: 'Καμία ζώνη ακόμη — προσθέστε μία πριν σχεδιάσετε τη διάταξη.',
    en: 'No zones yet — add one before drawing the layout.',
  },
  paymentTitle: { el: 'Online πληρωμή', en: 'Online payment' },
  payFull: { el: 'Πλήρες ποσό', en: 'Full price' },
  payHoldFee: { el: 'Μόνο το τέλος παρακράτησης', en: 'Hold fee only' },
  payNone: { el: 'Τίποτα — πληρωμή στο κατάστημα', en: 'Nothing — pay at the venue' },
  paymentNote: {
    el: 'Οι κρατήσεις του προσωπικού δεν περνούν ποτέ από πάροχο πληρωμών.',
    en: 'Staff reservations never touch a payment provider.',
  },
  saveChanges: { el: 'Αποθήκευση', en: 'Save changes' },
  layoutEditor: { el: 'Επεξεργαστής διάταξης', en: 'Layout editor' },
  layoutSaved: { el: 'Η διάταξη αποθηκεύτηκε', en: 'Layout saved' },
  // §18: a floor waits for its zones, so a draft outlives the tab it was in.
  unsavedDraft: {
    el: 'Έχετε μη αποθηκευμένες αλλαγές από',
    en: 'You have unsaved changes from',
  },
  resumeDraft: { el: 'Συνέχεια', en: 'Resume' },
  discardDraft: { el: 'Απόρριψη', en: 'Discard' },
  layoutFailed: { el: 'Η αποθήκευση της διάταξης απέτυχε', en: 'Saving the layout failed' },
  layoutHint: {
    el: 'Σχεδιάστε πρώτα τον χώρο με τη Θέση — οι νέες θέσεις είναι χωρίς ζώνη. Μετά διαλέξτε ζώνη και βάψτε τες με τη Ζώνη· η αποθήκευση δεν δέχεται θέση χωρίς ζώνη. Η αποθήκευση ξαναφτιάχνει τον χάρτη.',
    en: 'Draw the floor with Spot first — a new spot has no zone. Then pick a zone and brush the spots with Zone; a spot with no zone is refused on save. Saving rebuilds the live map.',
  },
  toolSpot: { el: 'Θέση', en: 'Spot' },
  toolZone: { el: 'Ζώνη', en: 'Zone' },
  zoneGone: { el: 'Χωρίς ζώνη', en: 'No zone' },
  layoutUnzoned: {
    el: 'Δώστε ζώνη σε κάθε θέση πριν την αποθήκευση — λείπει από τις:',
    en: 'Give every spot a zone before saving — these have none:',
  },
  toolDeck: { el: 'Ντεκ', en: 'Decking' },
  toolBar: { el: 'Μπαρ', en: 'Bar' },
  toolKitchen: { el: 'Κουζίνα', en: 'Kitchen' },
  toolWall: { el: 'Τοίχος', en: 'Wall' },
  toolErase: { el: 'Σβήσιμο', en: 'Erase' },
  nextNumber: { el: 'Επόμενος αριθμός', en: 'Next number' },
  zoneForSpots: { el: 'Ζώνη', en: 'Zone' },
  gridRows: { el: 'Σειρές', en: 'Rows' },
  gridCols: { el: 'Στήλες', en: 'Columns' },
  frontEdge: { el: 'Μπροστινό όριο', en: 'Front edge' },
  edgeNone: { el: 'Κανένα', en: 'None' },
  edgeSea: { el: 'Θάλασσα', en: 'Sea' },
  layoutNeedsZone: {
    el: 'Προσθέστε πρώτα μια ζώνη στις ρυθμίσεις, αλλιώς οι θέσεις δεν τιμολογούνται.',
    en: 'Add a zone in settings first, or the spots have no price.',
  },
  saveLayout: { el: 'Αποθήκευση διάταξης', en: 'Save layout' },
  discard: { el: 'Επαναφορά', en: 'Discard changes' },
  back: { el: 'Πίσω', en: 'Back' },
  invite: { el: 'Πρόσκληση μέλους', en: 'Invite member' },
  inviteTitle: { el: 'Πρόσκληση μέλους', en: 'Invite a member' },
  editMember: { el: 'Διόρθωση μέλους', en: 'Correct member' },
  emailResends: {
    el: 'Η αλλαγή email ακυρώνει την παλιά πρόσκληση και στέλνει νέα.',
    en: 'Changing the address cancels the old invitation and sends a new one.',
  },
  emailLocked: {
    el: 'Το email σύνδεσης δεν αλλάζει. Αφαιρέστε το μέλος και προσκαλέστε το ξανά.',
    en: 'The sign-in address never changes — remove the member and invite them again.',
  },
  name: { el: 'Όνομα', en: 'Name' },
  email: { el: 'Email', en: 'Email' },
  role: { el: 'Ρόλος', en: 'Role' },
  cancel: { el: 'Άκυρο', en: 'Cancel' },
  close: { el: 'Κλείσιμο', en: 'Close' },
  send: { el: 'Αποστολή πρόσκλησης', en: 'Send invitation' },
  active: { el: 'Ενεργός', en: 'Active' },
  invited: { el: 'Προσκεκλημένος', en: 'Invited' },
  inactive: { el: 'Ανενεργός', en: 'Inactive' },
  deactivate: { el: 'Απενεργοποίηση', en: 'Deactivate' },
  activate: { el: 'Ενεργοποίηση', en: 'Activate' },
  reactivate: { el: 'Επανενεργοποίηση', en: 'Reactivate' },
  remove: { el: 'Αφαίρεση', en: 'Remove' },
  removeConfirm: {
    el: 'Να αφαιρεθεί το μέλος από την ομάδα;',
    en: 'Remove this member from the team?',
  },
  ownerLocked: {
    el: 'Ο ιδιοκτήτης δεν αλλάζει από εδώ.',
    en: 'The owner is not edited here.',
  },
  joinTitle: { el: 'Καλώς ήρθατε στην ομάδα', en: 'Welcome to the team' },
  joinLead: {
    el: 'Επιλέξτε πώς θα συνδεθείτε. Το email της πρόσκλησης πρέπει να ταιριάζει.',
    en: 'Choose how to sign in. It has to be the address the invite was sent to.',
  },
  joinWithLink: { el: 'Συνέχεια με τον σύνδεσμο', en: 'Continue with this link' },
  joinMissing: {
    el: 'Ο σύνδεσμος πρόσκλησης λείπει ή έχει λήξει.',
    en: 'That invite link is missing or expired.',
  },
  noVenue: {
    el: 'Δεν ανήκετε ακόμη σε κατάστημα. Ζητήστε πρόσκληση από τον ιδιοκτήτη.',
    en: 'You do not belong to a venue yet. Ask the owner for an invitation.',
  },
  live: { el: 'Ζωντανά', en: 'Live' },
  connecting: { el: 'Σύνδεση…', en: 'Connecting…' },
  offline: { el: 'Εκτός σύνδεσης', en: 'Offline' },
  free: { el: 'Ελεύθερα', en: 'Free' },
  reserved: { el: 'Κρατημένα', en: 'Reserved' },
  occupied: { el: 'Κατειλημμένα', en: 'Occupied' },
  cleaning: { el: 'Καθαρισμός', en: 'Needs cleaning' },
  occupancy: { el: 'Πληρότητα', en: 'Occupancy' },
  occupations: { el: 'Χρήσεις', en: 'Occupations' },
  reservations: { el: 'Κρατήσεις', en: 'Reservations' },
  walkIn: { el: 'Χωρίς κράτηση', en: 'Walk-in' },
  staffOnly: { el: 'Μόνο από το προσωπικό', en: 'Staff-only' },
  sea: { el: 'ΘΑΛΑΣΣΑ', en: 'SEA' },
  deck: { el: 'ΔΙΑΔΡΟΜΟΣ', en: 'DECKING' },
  bar: { el: 'ΜΠΑΡ', en: 'BAR' },
  kitchen: { el: 'ΚΟΥΖΙΝΑ', en: 'KITCHEN' },
  wall: { el: 'ΤΟΙΧΟΣ', en: 'WALL' },
  today: { el: 'Σήμερα', en: 'Today' },
  prevDay: { el: 'Προηγούμενη μέρα', en: 'Previous day' },
  nextDay: { el: 'Επόμενη μέρα', en: 'Next day' },
  planningDay: {
    el: 'Προγραμματισμός — κρατήσεις για αυτή τη μέρα.',
    en: 'Planning — reservations for this day.',
  },
  pastDay: {
    el: 'Ιστορικό — μόνο για ανάγνωση.',
    en: 'History — read-only.',
  },
  minimapHint: {
    el: 'Όλο το κατάστημα — πατήστε ή σύρετε για μετακίνηση',
    en: 'The whole venue — tap or drag to move',
  },
  select: { el: 'Επιλογή', en: 'Select' },
  selectedCount: { el: 'επιλεγμένα', en: 'selected' },
  clearSelection: { el: 'Καθαρισμός επιλογής', en: 'Clear selection' },
  checkIn: { el: 'Άφιξη', en: 'Check in' },
  markOccupied: { el: 'Κατειλημμένο', en: 'Mark occupied' },
  markFree: { el: 'Ελεύθερο', en: 'Mark free' },
  markCleaning: { el: 'Θέλει καθάρισμα', en: 'Needs cleaning' },
  reserve: { el: 'Κράτηση', en: 'Reserve' },
  newReservation: { el: 'Νέα κράτηση', en: 'New reservation' },
  saveReservation: { el: 'Αποθήκευση κράτησης', en: 'Save reservation' },
  from: { el: 'Από', en: 'From' },
  to: { el: 'Έως', en: 'To' },
  arrival: { el: 'Ώρα άφιξης', en: 'Arrival time' },
  holdIncluded: { el: 'χωρίς χρέωση', en: 'included' },
  holdFee: { el: 'κράτημα', en: 'hold' },
  holdWindowClosed: {
    el: 'Πέρασε η τελευταία ώρα κρατήματος του καταστήματος',
    en: 'Past the venue’s last hold hour',
  },
  reserveAndCheckIn: { el: 'Κράτηση & άφιξη', en: 'Reserve & check in' },
  holdMaxed: {
    el: 'Η κράτηση φτάνει ήδη ως την τελευταία ώρα του καταστήματος.',
    en: 'This hold already runs to the venue’s latest hour.',
  },
  total: { el: 'Σύνολο', en: 'Total' },
  daysWord: { el: 'ημέρες', en: 'days' },
  reserveSaved: { el: 'Η κράτηση αποθηκεύτηκε', en: 'Reservation saved' },
  payingNow: { el: 'Πληρώνεται τώρα', en: 'Being paid for' },
  payingSelected: {
    el: 'Πληρώνονται online — αφαιρέστε τις για να συνεχίσετε:',
    en: 'Being paid for online — deselect them to carry on:',
  },
  payingNote: {
    el: 'Ένας επισκέπτης πληρώνει αυτή τη θέση online. Αν η πληρωμή δεν ολοκληρωθεί, ελευθερώνεται μόνη της.',
    en: 'A visitor is paying for this spot online. If the payment does not arrive it frees itself.',
  },
  yourSpot: { el: 'Η κράτησή σας', en: 'Your booking' },
  yours: { el: 'Δική σας', en: 'Yours' },
  spotBooked: {
    el: 'Η θέση είναι ήδη κρατημένη:',
    en: 'That spot is already booked:',
  },
  // The days of the range that are taken, so a date can be changed. §10
  onDays: { el: 'στις', en: 'on' },
  dropSpot: { el: 'Αφαίρεση θέσης', en: 'Remove spot' },
  spotInCheckout: {
    el: 'Η θέση πληρώνεται αυτή τη στιγμή online — ελευθερώνεται μόνη της αν η πληρωμή δεν ολοκληρωθεί:',
    en: 'That spot is being paid for online right now — it frees itself if the payment does not arrive:',
  },
  cancelRes: { el: 'Ακύρωση κράτησης', en: 'Cancel reservation' },
  resCancelled: { el: 'Η κράτηση ακυρώθηκε', en: 'Reservation cancelled' },
  checkedIn: { el: 'Ο πελάτης έφτασε', en: 'Guest checked in' },
  extendHold: { el: 'Παράταση κρατήματος', en: 'Extend hold' },
  extended: { el: 'Το κράτημα παρατάθηκε', en: 'Hold extended' },
  newTime: { el: 'Νέα ώρα', en: 'New time' },
  undo: { el: 'Αναίρεση', en: 'Undo' },
  restored: { el: 'Επαναφέρθηκε', en: 'Restored' },
  activity: { el: 'Δραστηριότητα & επαναφορά', en: 'Activity & restore' },
  activityEmpty: {
    el: 'Καμία αλλαγή σε κρατήσεις ακόμη.',
    en: 'No reservation has changed yet.',
  },
  restore: { el: 'Επαναφορά', en: 'Restore' },
  restoreBlocked: {
    el: 'Η θέση είναι ήδη κρατημένη για αυτές τις μέρες από:',
    en: 'The spot is already booked for those days by:',
  },
  restoreRefunded: {
    el: 'Τα χρήματα έχουν ήδη επιστραφεί στον πελάτη, οπότε η κράτηση δεν επαναφέρεται. Κάντε νέα κράτηση και εισπράξτε στο κατάστημα.',
    en: 'The money has already gone back to the guest, so this booking cannot be restored. Book it again and take payment at the venue.',
  },
  evReserved: { el: 'Κράτηση', en: 'Booked' },
  evCheckedIn: { el: 'Άφιξη', en: 'Checked in' },
  evCancelled: { el: 'Ακύρωση', en: 'Cancelled' },
  evExpired: { el: 'Έληξε', en: 'Expired' },
  bySystem: { el: 'αυτόματα', en: 'automatic' },
  views: { el: 'Προβολές', en: 'Views' },
  viewMap: { el: 'Χάρτης', en: 'Map' },
  viewList: { el: 'Λίστα', en: 'List' },
  viewCalendar: { el: 'Ημερολόγιο', en: 'Calendar' },
  searchSpots: { el: 'Αναζήτηση θέσης ή πελάτη…', en: 'Search spot or guest…' },
  all: { el: 'Όλα', en: 'All' },
  zone: { el: 'Ζώνη', en: 'Zone' },
  listEmpty: { el: 'Καμία θέση με αυτά τα κριτήρια.', en: 'No spot matches that.' },
  daysBack: { el: '14 μέρες πίσω', en: '14 days back' },
  daysForward: { el: '14 μέρες μπροστά', en: '14 days forward' },
  calendarHint: {
    el: 'Πατήστε ένα κενό κελί για κράτηση, ή μια μέρα για να τη δείτε.',
    en: 'Tap an empty cell to book it, or a day to open it.',
  },
  holdExpired: { el: 'Έληξε το κράτημα', en: 'Hold expired' },
  holdsExpired: { el: 'κρατήματα έληξαν', en: 'holds expired' },
  restoreFailed: {
    el: 'Η επαναφορά δεν έγινε — η θέση είναι πιασμένη.',
    en: 'That could not be restored — the spot is taken.',
  },
  makeStaffOnly: { el: '★ Μόνο από το προσωπικό', en: '★ Keep for staff' },
  bulkStaffOnly: { el: '★ Προσωπικό', en: '★ Staff' },
  bulkOnline: { el: 'Online', en: 'Online' },
  bulkOccupied: { el: 'Κατειλημμένα', en: 'Occupied' },
  bulkFree: { el: 'Ελεύθερα', en: 'Free' },
  bulkCleaning: { el: 'Καθάρισμα', en: 'Cleaning' },
  allowOnline: { el: 'Διαθέσιμο online', en: 'Allow online booking' },
  statusChanged: { el: 'Η κατάσταση άλλαξε', en: 'Status changed' },
  bulkChanged: { el: 'άλλαξαν', en: 'updated' },
  staffOnlyChanged: { el: 'Η διαθεσιμότητα άλλαξε', en: 'Availability changed' },
  actionFailed: { el: 'Η ενέργεια δεν ολοκληρώθηκε.', en: 'That action did not go through.' },
  spotLabel: { el: 'Θέση', en: 'Spot' },
  status: { el: 'Κατάσταση', en: 'Status' },
  reservation: { el: 'Κράτηση', en: 'Reservation' },
  noReservation: { el: 'Καμία κράτηση για αυτή τη μέρα.', en: 'No reservation for this day.' },
  guest: { el: 'Πελάτης', en: 'Guest' },
  phone: { el: 'Τηλέφωνο', en: 'Phone' },
  dates: { el: 'Ημερομηνίες', en: 'Dates' },
  heldUntil: { el: 'Κρατείται έως', en: 'Held until' },
  deposit: { el: 'Προκαταβολή', en: 'Deposit' },
  notes: { el: 'Σημειώσεις', en: 'Notes' },
  history: { el: 'Ιστορικό', en: 'History' },
  dayPrice: { el: 'Τιμή ημέρας', en: 'Day price' },
  mapNoLayout: {
    el: 'Αυτό το κατάστημα δεν έχει ακόμη κάτοψη. Ορίζεται στις ρυθμίσεις.',
    en: 'This venue has no floor plan yet. It is drawn in venue setup.',
  },
  loading: { el: 'Φόρτωση…', en: 'Loading…' },
  errorGeneric: { el: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.', en: 'Something went wrong. Try again.' },
  tryAgain: { el: 'Δοκιμάστε ξανά', en: 'Try again' },
  installTitle: { el: 'Βάλ’ το στην αρχική οθόνη', en: 'Add to your home screen' },
  installLead: {
    el: 'Ανοίγει σαν εφαρμογή, χωρίς τη γραμμή διευθύνσεων.',
    en: 'It opens like an app, with no address bar in the way.',
  },
  installAction: { el: 'Προσθήκη', en: 'Add' },
  installShow: { el: 'Δείξε μου', en: 'Show me' },
  installStep1: {
    el: 'Πάτησε το κουμπί Κοινή χρήση, στη γραμμή στο κάτω μέρος της οθόνης.',
    en: 'Tap the Share button, in the bar at the bottom of the screen.',
  },
  installStep2: {
    el: 'Κύλησε τη λίστα και διάλεξε «Προσθήκη στην αρχική οθόνη».',
    en: 'Scroll the list and choose “Add to Home Screen”.',
  },
  installStep3: { el: 'Πάτησε «Προσθήκη», πάνω δεξιά.', en: 'Tap “Add”, at the top right.' },
  installSafariOnly: {
    el: 'Μόνο από το Safari: το Chrome στο iPhone δεν εγκαθιστά εφαρμογές.',
    en: 'Safari only: Chrome on an iPhone cannot install apps.',
  },
  leavingForPayment: {
    el: 'Μεταφορά στη σελίδα πληρωμής…',
    en: 'Taking you to the payment page…',
  },
  offlineTitle: { el: 'Είστε εκτός σύνδεσης', en: 'You are offline' },
  offlineLead: {
    el: 'Ελέγξτε τη σύνδεσή σας στο internet. Ο χάρτης θα επανέλθει μόλις υπάρξει σήμα.',
    en: 'Check your internet connection. The floor comes back on its own once there is signal.',
  },
  serverDown: {
    el: 'Δεν μπορούμε να φτάσουμε τον διακομιστή αυτή τη στιγμή.',
    en: 'We cannot reach the server right now.',
  },
  error_google_unavailable: {
    el: 'Η σύνδεση με Google δεν είναι διαθέσιμη εδώ.',
    en: 'Google sign-in is not available on this deployment.',
  },
  error_google_failed: { el: 'Η σύνδεση με Google απέτυχε.', en: 'Google sign-in failed.' },
  error_email_mismatch: {
    el: 'Ο λογαριασμός Google δεν είναι η διεύθυνση της πρόσκλησης.',
    en: 'That Google account is not the address the invite was sent to.',
  },
  error_no_account: {
    el: 'Δεν υπάρχει λογαριασμός για αυτή τη διεύθυνση. Χρειάζεστε πρόσκληση.',
    en: 'No account for that address. You need an invitation first.',
  },
  noSignInMethod: {
    el: 'Δεν έχει ρυθμιστεί τρόπος σύνδεσης. Επικοινωνήστε με τον διαχειριστή.',
    en: 'No sign-in method is configured. Contact your administrator.',
  },
  error_magic_link_disabled: {
    el: 'Η σύνδεση με email είναι απενεργοποιημένη. Συνδεθείτε με Google.',
    en: 'Email sign-in is switched off. Use Google instead.',
  },
  error_link_invalid: {
    el: 'Ο σύνδεσμος έχει λήξει ή χρησιμοποιήθηκε ήδη.',
    en: 'That link has expired or was already used.',
  },
  menu: { el: 'Μενού', en: 'Menu' },
  venue: { el: 'Κατάστημα', en: 'Venue' },
  platformVenues: { el: 'Καταστήματα', en: 'Venues' },
  landingLead: {
    el: 'Ζωντανές κρατήσεις θέσεων για beach bar και μικρά καταστήματα.',
    en: 'Live spot reservations for beach bars and small venues.',
  },
  landingVenueHint: {
    el: 'Κάθε κατάστημα έχει τη δική του σελίδα κράτησης — ο σύνδεσμος είναι στο κατάστημα ή στο email επιβεβαίωσής σας.',
    en: 'Every venue has its own booking page — the link is at the venue, or in your confirmation email.',
  },
  landingSoon: {
    el: 'Η αναζήτηση καταστημάτων έρχεται σύντομα.',
    en: 'Browsing every venue is on its way.',
  },
  landingStaff: { el: 'Είστε προσωπικό; Συνδεθείτε στο', en: 'Staff sign in at' },
  heroTitle: { el: 'Βρες τη θέση σου στην παραλία', en: 'Find your spot by the sea' },
  heroSub: {
    el: 'Ζωντανή διαθεσιμότητα · άμεση κράτηση · online πληρωμή',
    en: 'Live availability · instant booking · pay online',
  },
  searchTown: { el: 'Πόλη ή περιοχή…', en: 'Town or area…' },
  searchBtn: { el: 'Αναζήτηση', en: 'Search' },
  searchSoon: {
    el: 'Η αναζήτηση περιοχής ανοίγει μαζί με τη δημόσια σελίδα (Φάση 8).',
    en: 'Location search opens with the public page (Phase 8).',
  },
  catAll: { el: 'Όλα', en: 'All' },
  freeToday: { el: 'ελεύθερα σήμερα', en: 'free today' },
  openVenue: { el: 'Άνοιγμα', en: 'Open venue' },
  platformManage: { el: 'Διαχείριση καταστημάτων', en: 'Manage venues' },
  platformBadge: { el: 'ΠΛΑΤΦΟΡΜΑ', en: 'PLATFORM' },
  platformBadgeHint: {
    el: 'Εργάζεστε ως διαχειριστής πλατφόρμας, χωρίς να ανήκετε στην ομάδα.',
    en: 'You are working as the platform operator, not as a member of this team.',
  },
  newVenue: { el: 'Νέο κατάστημα', en: 'New venue' },
  editVenue: { el: 'Επεξεργασία καταστήματος', en: 'Edit venue' },
  edit: { el: 'Επεξεργασία', en: 'Edit' },
  venueSlug: { el: 'Δημόσια διεύθυνση', en: 'Public address' },
  venueType: { el: 'Τύπος καταστήματος', en: 'Venue type' },
  spotKind: { el: 'Μονάδα κράτησης', en: 'Reservable unit' },
  location: { el: 'Τοποθεσία', en: 'Location' },
  nameKept: { el: 'Κρατήθηκε το όνομα του λογαριασμού:', en: 'Kept the name on file,' },
  nameKeptWhy: {
    el: 'αυτή η διεύθυνση έχει ήδη λογαριασμό· διορθώστε το από την οθόνη της ομάδας.',
    en: 'this address already has an account; correct it from the team screen.',
  },
  // §4: an owner is never on a team screen, so their name is corrected here.
  nameKeptWhyOwner: {
    el: 'αυτή η διεύθυνση έχει ήδη λογαριασμό· διορθώστε το εδώ.',
    en: 'this address already has an account; correct it here.',
  },
  renameOwner: { el: 'Διόρθωση ονόματος', en: 'Correct the name' },
  ownerRenameFailed: {
    el: 'Το όνομα δεν αποθηκεύτηκε',
    en: 'The name was not saved',
  },
  ownerDisplayName: { el: 'Όνομα ιδιοκτήτη', en: 'Owner display name' },
  findPlace: { el: 'Εύρεση', en: 'Find' },
  locationPlaceholder: {
    el: 'Όνομα καταστήματος ή σύνδεσμος Google Maps…',
    en: 'Venue name, or a Google Maps link…',
  },
  locationNoHits: {
    el: 'Κανένα αποτέλεσμα — δοκιμάστε αλλιώς, ή βάλτε την πινέζα στον χάρτη.',
    en: 'Nothing found — try another wording, or drop the pin on the map.',
  },
  locationPinned: { el: 'Σημείο:', en: 'Point:' },
  locationBadLink: {
    el: 'Αυτός ο σύνδεσμος δεν δείχνει τοποθεσία. Στο Google Maps: ανοίξτε το κατάστημα → Κοινοποίηση → Αντιγραφή συνδέσμου.',
    en: 'That link points at no place. In Google Maps: open the venue → Share → Copy link.',
  },
  locationSearchHint: {
    el: 'Γράψτε το όνομα και πατήστε Εύρεση, ή επικολλήστε σύνδεσμο Google Maps (Κοινοποίηση → Αντιγραφή συνδέσμου). Διευθύνσεις: OpenStreetMap.',
    en: 'Type a name and press Find, or paste a Google Maps link (Share → Copy link). Addresses: OpenStreetMap.',
  },
  locationOffline: {
    el: 'Ο χάρτης δεν απάντησε — γράψτε τη διεύθυνση και αποθηκεύστε κανονικά.',
    en: 'The map did not answer — type the address and save as usual.',
  },
  venueLogo: { el: 'Λογότυπο', en: 'Logo' },
  chooseFile: { el: 'Επιλογή αρχείου…', en: 'Choose a file…' },
  logoFailed: {
    el: 'Το λογότυπο δεν ανέβηκε — δεκτά PNG, JPEG ή WebP έως 256 KB.',
    en: 'The logo was not uploaded — PNG, JPEG or WebP up to 256 KB.',
  },
  save: { el: 'Αποθήκευση', en: 'Save' },
  registerOwner: { el: 'Ορισμός ιδιοκτήτη', en: 'Register owner' },
  manageOwners: { el: 'Διαχείριση ιδιοκτητών', en: 'Manage owners' },
  copyLayout: { el: 'Αντιγραφή διάταξης…', en: 'Copy layout from…' },
  copyLayoutTitle: { el: 'Αντιγραφή διάταξης και τιμών', en: 'Copy layout and pricing' },
  copyLayoutLead: {
    el: 'Αντιγράφονται ζώνες, τιμές, παρακράτηση, τρόπος πληρωμής και η διάταξη. Το όνομα, το slug, η ομάδα και οι κρατήσεις μένουν ως έχουν.',
    en: 'Zones, prices, the hold window, the payment mode and the layout are copied. Name, address, team and bookings stay as they are.',
  },
  copyLayoutFrom: { el: 'Αντιγραφή από', en: 'Copy from' },
  copyLayoutDone: { el: 'Η διάταξη αντιγράφηκε', en: 'Layout copied' },
  copyLayoutFailed: { el: 'Η αντιγραφή απέτυχε', en: 'Copying failed' },
  copyLayoutOccupied: {
    el: 'Αυτό το κατάστημα έχει ήδη θέσεις — αντιγράψτε σε ένα άδειο.',
    en: 'This venue already has spots — copy onto an empty one instead.',
  },
  copy: { el: 'Αντιγραφή', en: 'Copy' },
  addOwner: { el: 'Προσθήκη ιδιοκτήτη', en: 'Add owner' },
  ownerAddressNote: {
    el: 'Μια διεύθυνση που έχει συνδεθεί είναι η ταυτότητά της· για αλλαγή, αφαιρέστε την και προσθέστε τη σωστή.',
    en: 'An address someone has signed in with is their identity; to change one, remove it and add the right one.',
  },
  ownerLastOne: {
    el: 'Ένα κατάστημα κρατά πάντα έναν ιδιοκτήτη· πρόσθεσε τον επόμενο πριν αφαιρέσεις αυτόν.',
    en: 'A venue always keeps an owner: add the next one before taking this one off.',
  },
  ownerRemoveFailed: {
    el: 'Η αφαίρεση του ιδιοκτήτη δεν ολοκληρώθηκε.',
    en: 'That owner could not be removed.',
  },
  ownerMissing: { el: 'Χωρίς ιδιοκτήτη', en: 'No owner yet' },
  venueSaveFailed: { el: 'Το κατάστημα δεν αποθηκεύτηκε.', en: 'The venue was not saved.' },
  slugTaken: {
    el: 'Αυτή η διεύθυνση ανήκει ήδη σε άλλο κατάστημα.',
    en: 'That address already belongs to another venue.',
  },
  ownerFailed: { el: 'Ο ιδιοκτήτης δεν ορίστηκε.', en: 'The owner was not registered.' },
  ownerDuplicate: {
    el: 'Αυτή η διεύθυνση είναι ήδη ενεργό μέλος αυτού του καταστήματος.',
    en: 'That address is already an active member of this venue.',
  },
  inviteFailed: { el: 'Η πρόσκληση δεν στάλθηκε.', en: 'The invitation was not sent.' },
  inviteDuplicate: {
    el: 'Αυτή η διεύθυνση είναι ήδη ενεργό μέλος.',
    en: 'That address is already an active member.',
  },
  memberSaveFailed: { el: 'Η διόρθωση δεν αποθηκεύτηκε.', en: 'The correction was not saved.' },
  available: { el: 'Διαθέσιμη', en: 'Available' },
  spotsLabel: { el: 'Θέσεις', en: 'Spots' },
  unavailable: { el: 'Μη διαθέσιμη', en: 'Unavailable' },
  visitorLead: {
    el: 'Διάλεξε τη θέση σου και κράτησέ την σε λίγα δευτερόλεπτα.',
    en: 'Pick your spot and book it in seconds.',
  },
  selectHint: {
    el: 'Πάτησε τις διαθέσιμες θέσεις για να τις επιλέξεις.',
    en: 'Tap the available spots to choose them.',
  },
  bookNow: { el: 'Κράτηση', en: 'Book now' },
  perDay: { el: '/ημέρα', en: '/day' },
  payReserve: { el: 'Πληρωμή & κράτηση', en: 'Pay & reserve' },
  reserveOnly: { el: 'Ολοκλήρωση κράτησης', en: 'Confirm booking' },
  processing: { el: 'Επεξεργασία πληρωμής…', en: 'Processing payment…' },
  bookingTitle: { el: 'Η κράτησή σου', en: 'Your booking' },
  visitorClosed: {
    el: 'Η σημερινή κράτηση έκλεισε: πέρασε η τελευταία ώρα παρακράτησης.',
    en: 'Today is no longer bookable — the venue’s last hold hour has gone by.',
  },
  visitorClosedHint: {
    el: 'Διάλεξε μια επόμενη ημέρα ή έλα από το κατάστημα.',
    en: 'Pick a later day, or come by the venue.',
  },
  visitorFailed: { el: 'Η κράτηση δεν ολοκληρώθηκε.', en: 'The booking did not go through.' },
  visitorTaken: {
    el: 'Κάποια από τις θέσεις μόλις κρατήθηκε. Διάλεξε άλλη.',
    en: 'One of those spots was just taken. Please pick another.',
  },
  venueUnknown: { el: 'Δεν βρέθηκε κατάστημα εδώ.', en: 'There is no venue at this address.' },
  successTitle: { el: 'Η κράτησή σου επιβεβαιώθηκε', en: 'Your booking is confirmed' },
  successMsg: {
    el: 'Στείλαμε την επιβεβαίωση στο',
    en: 'We have sent the confirmation to',
  },
  showAtVenue: {
    el: 'Δείξε αυτόν τον κωδικό κατά την άφιξη.',
    en: 'Show this code on arrival.',
  },
  bookingCode: { el: 'Κωδικός κράτησης', en: 'Booking code' },
  manageBooking: { el: 'Διαχείριση κράτησης', en: 'Manage booking' },
  findBooking: { el: 'Αναζήτηση', en: 'Find booking' },
  bookingNotFound: {
    el: 'Δεν βρέθηκε κράτηση με αυτόν τον κωδικό και αυτό το email.',
    en: 'No booking under that code and address.',
  },
  bookingPending: {
    el: 'Εκκρεμεί πληρωμή',
    en: 'Payment not finished',
  },
  bookingPendingNote: {
    el: 'Η κράτηση κρατά τις θέσεις σας μέχρι να ολοκληρωθεί η πληρωμή. Αν κλείσατε τη σελίδα πληρωμής, συνεχίστε από εδώ — αλλιώς οι θέσεις ελευθερώνονται μόνες τους.',
    en: 'This booking holds your spots until the payment is finished. If you closed the payment page, carry on from here — otherwise the spots free themselves.',
  },
  resumePayment: { el: 'Συνέχεια πληρωμής', en: 'Continue payment' },
  payFailedTitle: { el: 'Η πληρωμή δεν ολοκληρώθηκε', en: 'Your payment did not go through' },
  payFailedNote: {
    el: 'Οι θέσεις σου κρατούνται για λίγο ακόμη. Δοκίμασε ξανά την πληρωμή, αλλιώς θα ελευθερωθούν.',
    en: 'Your spots are held for a few more minutes. Try the payment again, or they go back.',
  },
  payFailedNoWay: {
    el: 'Δοκίμασε ξανά από την αρχή σε λίγο, μόλις ελευθερωθούν οι θέσεις.',
    en: 'Start again in a few minutes, once the spots have gone back.',
  },
  bookingTooMany: {
    el: 'Πολλές προσπάθειες σε λίγη ώρα. Περιμένετε λίγο και δοκιμάστε ξανά.',
    en: 'Too many attempts just now. Wait a moment and try again.',
  },
  paidLabel: { el: 'Πληρώθηκε', en: 'Paid' },
  payExtend: { el: 'Πληρωμή & παράταση', en: 'Pay & extend' },
  extendOnlineNote: {
    el: 'Η παράταση πληρώνεται πάντα online.',
    en: 'An extension is always paid online.',
  },
  cancelBooking: { el: 'Ακύρωση κράτησης', en: 'Cancel booking' },
  keepBooking: { el: 'Διατήρηση κράτησης', en: 'Keep booking' },
  confirmCancel: { el: 'Επιβεβαίωση ακύρωσης', en: 'Confirm cancellation' },
  refundLine: { el: 'Επιστροφή', en: 'Refund' },
  refundFeeLine: { el: 'Κόστος συναλλαγής', en: 'Processing cost' },
  policyLine: { el: 'Πολιτική ακύρωσης', en: 'Cancellation policy' },
  tierFull: { el: 'Πλήρης επιστροφή', en: 'Full refund' },
  tierPartial: { el: 'Μερική επιστροφή', en: 'Partial refund' },
  tierNone: { el: 'Χωρίς επιστροφή', en: 'No refund' },
  cancelled: { el: 'Η κράτηση ακυρώθηκε', en: 'The booking is cancelled' },
  guestSeated: {
    el: 'Είσαι ήδη στη θέση σου, οπότε δεν υπάρχει κράτηση προς παράταση.',
    en: 'You are already at your spot, so there is no hold left to extend.',
  },
  fakeCheckout: { el: 'Δοκιμαστική πληρωμή', en: 'Test payment' },
  fakeCheckoutLead: {
    el: 'Αυτή είναι η σελίδα του δοκιμαστικού παρόχου πληρωμών.',
    en: 'This is the test payment provider’s own page.',
  },
  holdCountdown: { el: 'Οι θέσεις κρατούνται για', en: 'Your spots are held for' },
  holdCountdownNote: {
    el: 'Αν δεν πληρώσεις, ελευθερώνονται.',
    en: 'If you do not pay, they are released.',
  },
  holdLapsed: {
    el: 'Η κράτηση των θέσεων έληξε. Μπορεί να μην είναι πια διαθέσιμες.',
    en: 'The hold on your spots has run out — they may no longer be available.',
  },
  fakePay: { el: 'Πληρωμή', en: 'Pay' },
  fakeDecline: { el: 'Απόρριψη κάρτας', en: 'Card declined' },
  fakeCancel: { el: 'Ακύρωση πληρωμής', en: 'Cancel payment' },
  amountDue: { el: 'Ποσό', en: 'Amount' },
  policyFull: {
    el: 'Πλήρης επιστροφή έως',
    en: 'Full refund up to',
  },
  policyPartial: { el: 'Μερική επιστροφή έως', en: 'Partial refund up to' },
  policyNone: { el: 'Καμία επιστροφή εντός', en: 'No refund within' },
  hoursBefore: { el: 'ώρες πριν', en: 'hours before' },
  refundFeeNote: {
    el: 'Από κάθε επιστροφή αφαιρείται το κόστος συναλλαγής του παρόχου.',
    en: 'The provider’s processing cost is deducted from any refund.',
  },
} as const;

export type CopyKey = keyof typeof COPY;

export function translate(key: CopyKey, language: Language): string {
  return COPY[key][language];
}

export function storedLanguage(raw: string | null): Language {
  const parsed = languageSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'el';
}

// Private-mode browsers throw on storage; the choice is then just not remembered.
export function readLanguage(): Language {
  try {
    return storedLanguage(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'el';
  }
}

export function rememberLanguage(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    return;
  }
}

type LanguageState = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: CopyKey) => string;
};

const LanguageContext = createContext<LanguageState | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }): ReactElement {
  const [language, setStored] = useState<Language>(readLanguage);
  const value = useMemo<LanguageState>(
    () => ({
      language,
      setLanguage: (next: Language) => {
        rememberLanguage(next);
        setStored(next);
      },
      t: (key: CopyKey) => translate(key, language),
    }),
    [language],
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation(): LanguageState {
  const state = useContext(LanguageContext);
  if (state === null) throw new Error('useTranslation needs a LanguageProvider above it');
  return state;
}

export { LANGUAGES };
